import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { AccordionGroup, Loading } from '@portal/common/components';
import {
    DataCollectionDataPercentage,
    DataCollectionResource,
    DataProject,
} from '@portal/common/types';
import { withRouter } from '@portal/common/utility/hoc/with-router';

import api from '../../api';
import { getOrganizationId } from '../../store/organization/selectors';
import CollectionRow from './CollectionRow';

export const accordionDetailsStyle = {
    padding: 0,
};

export type DataTypePercentageRecord =
    DataCollectionDataPercentage['data_type_percentage'][keyof DataCollectionDataPercentage['data_type_percentage']];

export type DataTypeRecord = [string, DataTypePercentageRecord];

type State = {
    dataCollections: null | DataCollectionResource[];
    dataProjects: null | DataProject[];
};

const defaultState: State = {
    dataCollections: null,
    dataProjects: null,
};

const DataLicenseTab = memo(() => {
    const [data, setData] = React.useState<State>(defaultState);
    const organizationId = useSelector(getOrganizationId);

    const licenseCollections: any[] | [number, string, number, [string, unknown][], string][] =
        useMemo(() => {
            if (!data.dataCollections || !data.dataProjects) {
                return [];
            }
            return data.dataCollections.map((resource) => {
                const {
                    data_percentage: { data_type_percentage, total_percentage },
                    data_project_id,
                    id,
                    name,
                } = resource;

                const dataTypeRecords = Object.entries(data_type_percentage);
                const dataProjectName = data.dataProjects.find(
                    (project) => project.id === data_project_id
                )?.name;

                return [id, name, total_percentage, dataTypeRecords, dataProjectName];
            });
        }, [data]);

    useEffect(() => {
        const fetchData = async () => {
            const { results: dataCollections } =
                await api.organizationGroupContent.getDataCollections({
                    organizationId,
                    order_by: 'name',
                    fields: 'data_percentage',
                });
            const dataProjects = await api.dataProjects.get();
            setData({ dataCollections, dataProjects });
        };

        fetchData().catch(console.error);
    }, []);

    if (!data.dataCollections || !data.dataProjects) {
        return <Loading />;
    }

    return (
        <AccordionGroup>
            {licenseCollections.map((collection) => (
                <CollectionRow key={collection.id} collection={collection} />
            ))}
        </AccordionGroup>
    );
});

export default withRouter(DataLicenseTab);
